/**
 * O aviso de bloqueio (14/10/2026) — `docs/app-unificado/PLANO-framework-de-bloqueio.md`.
 *
 * Um só, na raiz do app: aparece quando a API recusa uma porta (403
 * `BLOQUEADO`) ou quando a tela toca num item travado (`mostrarBloqueio`). O
 * texto e o botão são os da regra da marca. O botão que abre formulário abre
 * a janela por cima de qualquer tela — inclusive o onboarding, que fica fora
 * do portal da paciente.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Drawer } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconLock, IconX } from '@tabler/icons-react';
import { onBloqueio } from '@eva/domain';
import { registrarMomento } from '../analise/contentsquare';
import { registrarUso } from '../analise/uso';
import type { BloqueioAtivo } from '@eva/schemas';
import { PatientButton } from '@eva/ui';
import { JanelaDoFormulario } from '../portals/eu/profile/JanelaDoFormulario';

const FONTE_TITULO = 'var(--eva-font-titulo)';

export function AnfitriaoDosBloqueios() {
  const [bloqueio, setBloqueio] = useState<BloqueioAtivo | null>(null);
  const [formulario, setFormulario] = useState<string | null>(null);
  useEffect(
    () =>
      onBloqueio((b) => {
        setBloqueio(b);
        // Para a análise de uso: qual bloqueio ela encontrou (o tipo e o título do aviso).
        registrarMomento('Bloqueio visto', `${b.alvo.tipo}: ${b.aviso.titulo}`);
        registrarUso('bloqueio_visto', { regra: b.regraId, alvo: b.alvo.tipo });
      }),
    [],
  );

  const aviso = bloqueio?.aviso;
  const fechar = () => setBloqueio(null);
  const agir = () => {
    const acao = aviso?.botao?.acao;
    if (bloqueio && aviso?.botao) {
      registrarMomento('Bloqueio: ação', `${bloqueio.alvo.tipo}: ${aviso.botao.rotulo}`);
      registrarUso('bloqueio_acao', { regra: bloqueio.regraId, alvo: bloqueio.alvo.tipo, acao: aviso.botao.acao?.type ?? 'NONE' });
    }
    fechar();
    if (!acao) return;
    if (acao.type === 'OPEN_FORM') setFormulario(acao.formId);
    else if (acao.type === 'EXTERNAL_URL') {
      const aberta = window.open(acao.url, '_blank', 'noopener,noreferrer');
      if (!aberta) notifications.show({ color: 'red', message: 'Não foi possível abrir o link.' });
    }
  };

  return (
    <>
      <Drawer
        opened={!!bloqueio}
        onClose={fechar}
        position="bottom"
        size="auto"
        withCloseButton={false}
        padding={0}
        overlayProps={{ backgroundOpacity: 0.55 }}
        styles={{
          content: { maxWidth: 520, margin: '0 auto', backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '90%', flex: '0 0 100%', height: 'auto' },
          body: { padding: '10px 20px calc(24px + env(safe-area-inset-bottom, 0px))' },
        }}
      >
        {aviso && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: FONTE_TITULO }}>
            <div style={estilos.alca} aria-hidden />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <span aria-hidden style={estilos.icone}>
                <IconLock size={24} stroke={1.8} color="var(--eva-brand-accent-text)" />
              </span>
              <h2 style={estilos.titulo}>{aviso.titulo}</h2>
              <button type="button" className="eu-botao" aria-label="Fechar" onClick={fechar} style={estilos.fechar}>
                <IconX size={18} stroke={2} />
              </button>
            </div>
            <p style={estilos.texto}>{aviso.texto}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {aviso.botao && (
                <PatientButton title={aviso.botao.rotulo} style={{ background: 'var(--eva-brand-accent)', color: '#FFFFFF' }} onClick={agir} />
              )}
              <PatientButton
                title={aviso.fechar}
                variant="secondary"
                onClick={fechar}
                style={{ background: 'var(--eva-brand-tom-claro)', color: 'var(--eva-brand-accent-text)' }}
              />
            </div>
          </div>
        )}
      </Drawer>
      {formulario && <JanelaDoFormulario formId={formulario} onFechar={() => setFormulario(null)} onEnviado={() => setFormulario(null)} />}
    </>
  );
}

const estilos: Record<string, CSSProperties> = {
  alca: { width: 40, height: 4, borderRadius: 2, background: '#E5E7EB', margin: '0 auto -4px' },
  icone: { width: 48, height: 48, flex: '0 0 48px', borderRadius: '50%', background: 'var(--eva-brand-tom-claro)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  titulo: { flex: 1, margin: '2px 0 0', fontSize: 20, lineHeight: '26px', fontWeight: 700, color: '#111827', textWrap: 'balance' } as CSSProperties,
  texto: { margin: 0, fontSize: 15, lineHeight: '22px', color: '#4B5563' },
  fechar: { width: 36, height: 36, flex: '0 0 36px', borderRadius: '50%', background: '#F3F4F6', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
